import { useCallback, useMemo, useState } from "react";
import styles from "./GenerateKit.module.css";
import { KitProductItem } from "./KitProductItem";
import { AddKitToCartDialog } from "./AddKitToCartDialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../config";
import { toast } from "react-toastify";

const formatPrice = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n ?? 0);

export const KitCard = ({
  kit,
  kitIndex,
  optionLabel,
  budget = 0,
  productSplit,
  onReplace,
}) => {
  const [openSelectUserDialog, setOpenSelectUserDialog] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { createNewCart, userId, cartId } = useMemo(() => {
    const createNewCart = searchParams.get("newCart") === "true";
    const userId = searchParams.get("userId");
    const cartId = searchParams.get("cartId");
    return { createNewCart, userId, cartId };
  }, [searchParams]);

  const handleAddToCart = useCallback(async () => {
    if (!!userId && createNewCart === true) {
      const response = await axios.post(`${API}/Admin/carts`, {
        userId: userId,
        products: kit.products.map((p) => ({
          productId: p.productId,
          quantity: 1,
        })),
      });
      if (response.status === 201 || response.status === 200) {
        toast.success("Cart created successfully");
        navigate(`/users/${userId}/carts`);
      } else {
        toast.error("Failed to create cart");
      }
    } else if (!!userId && !!cartId) {
      const response = await axios.post(
        `${API}/Admin/carts/${cartId}/products`,
        {
          products: kit.products.map((p) => ({
            productId: p.productId,
            quantity: 1,
          })),
        },
      );
      if (response.status === 201 || response.status === 200) {
        toast.success("Products added to cart successfully");
        navigate(`/users/${userId}/carts/${cartId}`);
      } else {
        toast.error("Failed to add products to cart");
      }
    } else {
      setOpenSelectUserDialog(true);
    }
  }, [createNewCart, userId, cartId, navigate, kit.products]);
  const products = kit?.products ?? [];
  const totalCost = kit?.totalCost ?? 0;

  const budgetDiffPercent =
    budget > 0 ? ((totalCost - budget) / budget) * 100 : 0;
  const isUnderBudget = budgetDiffPercent <= 0;

  return (
    <div className={styles.kitCard}>
      <div className={styles.kitCardHeader}>
        <h3 className={styles.kitCardTitle}>{optionLabel}</h3>
        <div className={styles.kitCardTotal}>{formatPrice(totalCost)}</div>
      </div>
      <div className={styles.kitProductList}>
        {products.map((product) => (
          <KitProductItem
            key={product.productId}
            product={product}
            kitIndex={kitIndex}
            productSplit={productSplit}
            onReplace={onReplace}
          />
        ))}
      </div>
      <div className={styles.kitCardFooter}>
        <div className={styles.kitCardSubtotal}>
          <span>Number of products</span>
          <span className={styles.kitCardTotalValue}>{products.length}</span>
        </div>
        <div className={styles.kitCardSubtotal}>
          <span>Kit Total</span>
          <span className={styles.kitCardTotalValue}>
            {formatPrice(totalCost)}
          </span>
        </div>
        {budget > 0 && (
          <div className={styles.kitCardSubtotal}>
            <span>Budget Difference</span>
            <span
              className={`${styles.kitCardBudgetDiffValue} ${
                isUnderBudget
                  ? styles.kitCardBudgetDiffUnder
                  : styles.kitCardBudgetDiffOver
              }`}
            >
              {isUnderBudget ? "" : "+"}
              {budgetDiffPercent.toFixed(1)}%
            </span>
          </div>
        )}
        <button
          className={styles.regenerateButton}
          style={{
            width: "100%",
            alignItems: "center",
            textAlign: "center",
            justifyContent: "center",
            display: "flex",
          }}
          onClick={handleAddToCart}
        >
          Add to Cart
        </button>
      </div>
      <AddKitToCartDialog
        show={openSelectUserDialog}
        onClose={() => setOpenSelectUserDialog(false)}
        kit={kit}
      />
    </div>
  );
};
